/*!
=========================================================
* Regiform IMS - Account Data Transformers
=========================================================
* Utility functions to transform between backend API
* response structure and frontend component expectations
=========================================================
*/

/**
 * Transform backend ChartOfAccountDTO to frontend format
 * @param {Object} backendAccount - Account from API
 * @returns {Object} Transformed account for frontend
 */
export const transformAccountFromAPI = (backendAccount) => {
  if (!backendAccount) return null;

  return {
    id: backendAccount.id,
    code: backendAccount.accountCode,
    name: backendAccount.accountName,
    accountType: backendAccount.accountType,
    parentAccountId: backendAccount.parentAccountId,
    parentAccountName: backendAccount.parentAccountCode, // Backend provides code, frontend expects name
    description: backendAccount.description,
    isActive: backendAccount.status === "ACTIVE",
    normalBalance: backendAccount.normalBalanceSide,

    // Category information
    categoryId: backendAccount.accountCategory?.id,
    categoryName: backendAccount.accountCategory?.categoryName,

    // Hierarchy helpers (calculated from childAccounts)
    hasChildren: backendAccount.childAccounts?.length > 0,
    childCount: backendAccount.childAccounts?.length || 0,
    level: 0, // Will be calculated during tree building

    // Financial data (from statistics)
    currentBalance: backendAccount.currentBalance || 0,
    transactionCount: backendAccount.transactionCount || 0,
    lastTransactionDate: backendAccount.lastTransactionDate || null,
    isSystemAccount: false, // Not tracked by backend
    financialYear: backendAccount.financialYear || null,

    // Audit
    createdAt: backendAccount.createdAt,
    updatedAt: backendAccount.updatedAt,
    createdBy: backendAccount.createdBy,
    updatedBy: backendAccount.updatedBy,
  };
};

/**
 * Transform array of backend accounts to frontend format
 * @param {Array} backendAccounts - Accounts from API
 * @returns {Array} Transformed accounts
 */
export const transformAccountsFromAPI = (backendAccounts = []) => {
  if (!Array.isArray(backendAccounts)) {
    return [];
  }
  return backendAccounts.map(transformAccountFromAPI).filter(acc => acc !== null);
};

/**
 * Transform frontend account data to backend CREATE request format
 * @param {Object} frontendData - Account data from form
 * @returns {Object} Data for POST /api/accounts
 */
export const transformAccountToCreateAPI = (frontendData) => {
  if (!frontendData) return null;

  return {
    accountCode: frontendData.code,
    accountName: frontendData.name,
    accountType: frontendData.accountType,
    accountCategoryId: frontendData.categoryId,
    parentAccountId: frontendData.parentAccountId || null,
    normalBalanceSide:
      frontendData.normalBalance ||
      determineNormalBalance(frontendData.accountType),
    description: frontendData.description || "",
    status: frontendData.isActive !== false ? "ACTIVE" : "INACTIVE",
  };
};

/**
 * Transform frontend account data to backend UPDATE request format
 * @param {Object} frontendData - Updated account data
 * @returns {Object} Data for PUT /api/accounts/{id}
 */
export const transformAccountToUpdateAPI = (frontendData) => {
  if (!frontendData) return null;

  // Only include updatable fields (code, type, parent are immutable)
  return {
    accountName: frontendData.name,
    description: frontendData.description || "",
    status: frontendData.isActive ? "ACTIVE" : "INACTIVE",
  };
};

/**
 * Helper: Determine normal balance side from account type
 * @param {string} accountType - Account type (ASSET, LIABILITY, etc.)
 * @returns {string} DEBIT or CREDIT
 */
const determineNormalBalance = (accountType) => {
  const debitTypes = ["ASSET", "EXPENSE"];
  return debitTypes.includes(accountType) ? "DEBIT" : "CREDIT";
};

/**
 * Transform backend AccountTypeDTO to frontend format
 * @param {Object} backendType - Account type from API
 * @returns {Object} Transformed account type with metadata
 */
export const transformAccountTypeFromAPI = (backendType) => {
  if (!backendType) return null;

  const typeRanges = {
    ASSET: "1000-1999",
    LIABILITY: "2000-2999",
    EQUITY: "2000-3999",
    REVENUE: "4000-4999",
    EXPENSE: "5000-5999",
  };

  const normalBalances = {
    ASSET: "DEBIT",
    LIABILITY: "CREDIT",
    EQUITY: "CREDIT",
    REVENUE: "CREDIT",
    EXPENSE: "DEBIT",
  };

  return {
    value: backendType.typeName,
    label: backendType.typeName,
    codeRange: typeRanges[backendType.typeName] || "",
    normalBalance: normalBalances[backendType.typeName] || "DEBIT",
    description: backendType.description,
  };
};

/**
 * Transform array of account types
 * @param {Array} backendTypes - Account types from API
 * @returns {Array} Transformed account types
 */
export const transformAccountTypesFromAPI = (backendTypes = []) => {
  if (!Array.isArray(backendTypes)) {
    return [];
  }
  return backendTypes
    .map(transformAccountTypeFromAPI)
    .filter(type => type !== null);
};

/**
 * Calculate hierarchy levels for accounts
 * Assigns a level property to each account based on parent relationships
 * @param {Array} accounts - Accounts to assign levels to
 * @returns {Array} Accounts with level property set
 */
export const calculateHierarchyLevels = (accounts) => {
  if (!Array.isArray(accounts) || accounts.length === 0) {
    return accounts;
  }

  const accountMap = new Map(accounts.map(acc => [acc.id, acc]));

  const calculateLevel = (account) => {
    // Already calculated
    if (account.level !== undefined && account.level !== null) {
      return account.level;
    }

    // Root account (no parent)
    if (!account.parentAccountId) {
      account.level = 0;
      return 0;
    }

    // Find parent
    const parent = accountMap.get(account.parentAccountId);
    if (!parent) {
      // Parent not found (orphaned account) - treat as root
      account.level = 0;
      return 0;
    }

    // Recursively calculate parent's level
    const parentLevel = calculateLevel(parent);
    account.level = parentLevel + 1;
    return account.level;
  };

  // Calculate level for all accounts
  accounts.forEach(calculateLevel);

  return accounts;
};

/**
 * Build hierarchical tree structure from flat account list
 * @param {Array} accounts - Flat list of accounts
 * @returns {Array} Root accounts with nested children
 */
export const buildAccountTree = (accounts) => {
  if (!Array.isArray(accounts) || accounts.length === 0) {
    return [];
  }

  const accountMap = new Map(accounts.map(acc => [acc.id, { ...acc, children: [] }]));
  const roots = [];

  accounts.forEach(account => {
    const node = accountMap.get(account.id);

    if (!account.parentAccountId) {
      // Root account
      roots.push(node);
    } else {
      // Child account - add to parent's children
      const parent = accountMap.get(account.parentAccountId);
      if (parent) {
        parent.children.push(node);
      } else {
        // Orphaned account - treat as root
        roots.push(node);
      }
    }
  });

  return roots;
};

/**
 * Flatten hierarchical tree structure back to flat list
 * @param {Array} tree - Tree structure (with children array)
 * @returns {Array} Flat list of accounts
 */
export const flattenAccountTree = (tree) => {
  if (!Array.isArray(tree)) {
    return [];
  }

  const flattened = [];

  const traverse = (node) => {
    if (!node) return;

    const { children, ...accountData } = node;
    flattened.push(accountData);

    if (Array.isArray(children)) {
      children.forEach(child => traverse(child));
    }
  };

  tree.forEach(root => traverse(root));

  return flattened;
};
